/**
 * All optional/portal-dependent behavior lives behind a flag here so Phase 1
 * ships and runs correctly with every flag OFF, using only TRERC/NAR/Census
 * style static-snapshot sources. Flip a flag to `true` once the matching
 * adapter's live/legal data path is ready -- no other code needs to change.
 */
export interface FeatureFlags {
  /** Redfin Data Center adapter (optional third-party portal). */
  ENABLE_REDFIN: boolean;
  /**
   * Zillow Research adapter -- now real (zillowNeighborhoodMapAdapter.ts),
   * feeding the neighborhood layer's ZHVI data for the four target metro
   * cores. Also gates the neighborhood UI layer itself (MapControls'
   * dropdown option, MapZoomGeoLevelController's metro-bounded zoom
   * threshold) since there's no other consumer of this flag.
   */
  ENABLE_ZILLOW: boolean;
  /** ZIP/ZCTA choropleth layer (Phase 2 geography granularity). */
  ENABLE_ZIP_LAYER: boolean;
  /** Side-by-side two-geography comparison mode. */
  ENABLE_COMPARE_MODE: boolean;
  /**
   * TRERC/NAR/Census-ACS synthetic sample-snapshot adapters
   * (src/data/adapters/{trerc,nar,censusAcs}Adapter.ts). These were Phase
   * 1's original always-on sources, generated by
   * scripts/generate-sample-data.mjs -- clearly labeled "SAMPLE DATA" in
   * the UI, but still fabricated numbers, not real published figures (see
   * README.md's "Known placeholder data" section). Now that
   * mayBackendAdapter.ts provides real, live per-county data for (most of)
   * Texas via the actual backend, these three stay OFF by default so the
   * map never silently mixes fake numbers into what's shown -- flip this
   * on only for local demoing when the real backend isn't reachable and
   * seeing *something* on the map matters more than every figure being
   * real (the freshness badge will still correctly say SAMPLE DATA
   * whenever this is on and contributing).
   */
  ENABLE_SAMPLE_FALLBACK: boolean;
}

export const featureFlags: FeatureFlags = {
  ENABLE_REDFIN: false,
  ENABLE_ZILLOW: true,
  // Statewide ZIP layer (backend /api/stats/zip-map + zip-aware
  // mayBackendAdapter.ts + ZipLayer.tsx + zoom-driven auto-switching in
  // MapZoomGeoLevelController.tsx) is code-complete. Real ZIP metrics will
  // load as soon as the backend endpoint is deployed; real ZIP *boundaries*
  // won't render until scripts/fetch-zips-500k.mjs is run once (see that
  // script's header comment) -- until then ZipLayer.tsx shows nothing
  // rather than fabricated shapes, per this project's honesty-over-
  // placeholder rule (README.md "Known placeholder data").
  ENABLE_ZIP_LAYER: true,
  ENABLE_COMPARE_MODE: false,
  ENABLE_SAMPLE_FALLBACK: false,
};
